import { useEffect, useRef, useState } from 'preact/hooks';
import type { Flow, Step } from '../flow';
import { uiCopy } from '../copy';
import { errorCopy } from '../errors';
import { useFlowState } from './useFlow';
import { openFullEditor } from '../kc';
import { Compare } from './Compare';
import { exampleFor } from './examples';
import { HeroArt, IconAlert, IconCheck, IconClose, IconLock, IconPencil, IconPhoto, IconUpload } from './icons';

const isBusy = (step: Step) => step.name === 'working' || step.name === 'applying' || step.name === 'preparing';

export function ModalRoot({ flow }: { flow: Flow }) {
  const state = useFlowState(flow);
  const fileInput = useRef<HTMLInputElement>(null);
  const sheet = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!state.open) return;
    // Pagina eronder niet laten meescrollen; focus in de dialoog; Esc sluit.
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow] as const;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    sheet.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') flow.close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      html.style.overflow = prev[0];
      body.style.overflow = prev[1];
      window.removeEventListener('keydown', onKey);
    };
  }, [state.open, flow]);

  /** Moet synchroon binnen een klik worden aangeroepen, anders opent de bestandskiezer niet. */
  const pick = () => {
    const input = fileInput.current;
    if (!input) return;
    input.value = '';
    input.click();
  };

  const onFile = (e: Event) => {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (file) void flow.pickFile(file);
  };

  const step = state.step;
  const t = uiCopy(flow.shopId);
  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.heic,.heif"
        class="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={onFile}
      />
      {state.open && (
        <div
          class="overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isBusy(step)) flow.close();
          }}
        >
          <div
            ref={sheet}
            class={step.name === 'intro' || step.name === 'style' ? 'sheet sheet--wide' : 'sheet'}
            role="dialog"
            aria-modal="true"
            aria-labelledby="aps-title"
            tabIndex={-1}
          >
            <div class={step.name === 'intro' ? 'head head--intro' : 'head'}>
              {step.name === 'intro' ? (
                <button type="button" class="btn-cancel" onClick={() => flow.close()}>
                  {t.cancel}
                </button>
              ) : null}
              <h2 class="head__title" id="aps-title">
                {step.name === 'style' && flow.variant === 'scenes' ? t.titles.scenes : t.titles[step.name]}
              </h2>
              {step.name === 'intro' ? (
                <span class="head__balance" aria-hidden="true" />
              ) : (
                <button type="button" class="icon-btn" aria-label={t.close} onClick={() => flow.close()}>
                  <IconClose />
                </button>
              )}
            </div>
            <StepView step={step} flow={flow} pick={pick} />
          </div>
        </div>
      )}
    </>
  );
}

interface StepProps {
  flow: Flow;
  pick: () => void;
}

function StepView({ step, flow, pick }: StepProps & { step: Step }) {
  switch (step.name) {
    case 'intro':
      return <Intro pick={pick} flow={flow} />;
    case 'preparing':
      return <Busy text={uiCopy(flow.shopId).readingPhoto} />;
    case 'style':
      return <StyleStep step={step} flow={flow} pick={pick} />;
    case 'working':
      return <Working step={step} flow={flow} />;
    case 'compare':
      return <CompareStep step={step} flow={flow} pick={pick} />;
    case 'applying':
      return <Busy text={uiCopy(flow.shopId).placing} />;
    case 'done':
      return <Done step={step} flow={flow} pick={pick} />;
    case 'error':
      return <ErrorView step={step} flow={flow} pick={pick} />;
  }
}

function Intro({ pick, flow }: { pick: () => void; flow: Flow }) {
  const { library } = useFlowState(flow);
  const t = uiCopy(flow.shopId);
  const unavailable = flow.availabilityError ? errorCopy(flow.availabilityError, undefined, flow.shopId) : null;
  const fixed = flow.catalog?.fixedLook ? flow.choices.find((look) => look.id === flow.catalog.fixedLook) : undefined;
  const name = flow.catalog?.catalog?.name;
  const example = fixed ? exampleFor(fixed, flow.variant) : undefined;
  const saved = library.length > 0 ? (
    <section class="library" aria-label={t.libraryLabel}>
      <h3 class="library__title">{t.libraryTitle}</h3>
      <p class="library__note">{t.libraryNote}</p>
      <div class="library__row">
        {library.map((item) => (
          <article class="library__card" key={item.id}>
            <img src={item.previewUrl} alt="" />
            <span class="library__label">{item.label}</span>
            <button type="button" class="btn btn--primary btn--sm" onClick={() => void flow.useSaved(item.id)}>
              {t.apply}
            </button>
          </article>
        ))}
      </div>
    </section>
  ) : null;
  return (
    <>
      <div class="body">
        {unavailable ? (
          <div class="err" role="alert">
            <h3>{unavailable.title}</h3>
            <p>{unavailable.message}</p>
          </div>
        ) : <div class="intro-grid">
          <section class="tips-panel">
            {name ? <p class="panel-name">{name}</p> : null}
            {fixed ? <>
              <h3>{fixed.label}</h3>
              {fixed.description ? <p class="panel-copy">{fixed.description}</p> : null}
              {example ? <img class="fixed-example" src={example} alt={t.exampleAlt(fixed.label)} /> : null}
            </> : (
              <div class="hero">
                <HeroArt label={t.heroAria} />
              </div>
            )}
            <h3 class="tips-heading">{t.tipsHeading}</h3>
            <ul class="tips">
              <li>
                <IconCheck />
                {t.tipSharp}
              </li>
              <li>
                <IconCheck />
                {t.tipSubject}
              </li>
              <li>
                <IconCheck />
                {t.tipFilters}
              </li>
            </ul>
            <p class="fine">
              <IconLock />
              {t.privacy}
            </p>
          </section>
          <section class="drop">
            <span class="drop__icon">
              <IconUpload />
            </span>
            <p class="drop__meta">{t.formats}</p>
            <button type="button" class="btn btn--primary" onClick={pick}>
              <IconPhoto />
              {t.choosePhoto}
            </button>
          </section>
        </div>}
        {saved}
      </div>
    </>
  );
}

function StyleStep({ step, flow, pick }: StepProps & { step: Extract<Step, { name: 'style' }> }) {
  const cards = flow.choices;
  return (
    <>
      <div class="body">
        {flow.catalog?.catalog?.name ? <h3>{flow.catalog.catalog.name}</h3> : null}
        <div class="style-photo">
          <img src={step.photo.url} alt={uiCopy(flow.shopId).chosenAlt} />
        </div>
        <div class="style-grid">
          {cards.map((style) => (
            <button type="button" class="style-card" key={style.id} onClick={() => void flow.chooseStyle(style.id)}>
              {exampleFor(style, flow.variant) ? <img src={exampleFor(style, flow.variant)} alt="" /> : null}
              <span class="style-card__text">
                <b>{style.label}</b>
                <span>{style.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div class="foot">
        <button
          type="button"
          class="btn btn--ghost"
          onClick={() => {
            flow.chooseOther();
            pick();
          }}
        >
          {uiCopy(flow.shopId).otherPhoto}
        </button>
      </div>
    </>
  );
}

function Busy({ text }: { text: string }) {
  return (
    <div class="body center" aria-live="polite">
      <div class="spinner" />
      <p class="status">{text}</p>
    </div>
  );
}

function Working({ step, flow }: { step: Extract<Step, { name: 'working' }>; flow: Flow }) {
  const t = uiCopy(flow.shopId);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 400);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, (now - step.startedAt) / 1000);
  // Tijdsgebaseerde schatting: snel naar ~12% tijdens uploaden, daarna asymptotisch naar 94%.
  const pct =
    step.phase === 'uploading' ? Math.min(12, 3 + elapsed * 5) : Math.min(94, 12 + 82 * (1 - Math.exp(-elapsed / 8)));
  const message =
    step.phase === 'uploading'
      ? t.sendingPhoto
      : t.workMessages[Math.min(t.workMessages.length - 1, Math.floor(elapsed / 4))];
  return (
    <>
      <div class="body">
        <div class="stage">
          <img src={step.photo.url} alt={t.yourPhotoAlt} width={step.photo.width} height={step.photo.height} />
          <div class="wash" />
          <div class="shine" />
        </div>
        <p class="status" aria-live="polite">
          {message}
        </p>
        <div class="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-valuetext={`${Math.round(pct)} ${t.percent}`}>
          <span style={{ width: `${pct}%` }} />
        </div>
        <p class="progress-label">{Math.round(pct)}%</p>
        <p class="hint">
          {elapsed > 45 ? t.slow : t.usual}
        </p>
      </div>
      <div class="foot">
        <button type="button" class="btn btn--ghost" onClick={() => flow.cancel()}>
          {t.cancel}
        </button>
      </div>
    </>
  );
}

function CompareStep({ step, flow, pick }: StepProps & { step: Extract<Step, { name: 'compare' }> }) {
  const t = uiCopy(flow.shopId);
  return (
    <>
      <div class="body">
        <Compare
          before={step.photo.url}
          after={step.result.url}
          ratio={step.result.width / step.result.height}
          styleLabel={flow.lookLabel(step.styleId)}
          copy={t}
        />
        <p class="compare-hint">{t.compareHint}</p>
      </div>
      <div class="foot">
        <div class="foot__pair">
          <button type="button" class="btn btn--ghost" onClick={() => void flow.retrySame()}>
            {t.again}
          </button>
          <button
            type="button"
            class="btn btn--ghost"
            onClick={() => {
              flow.chooseOther();
              pick();
            }}
          >
            {t.otherPhoto}
          </button>
        </div>
        <button type="button" class="btn btn--primary" onClick={() => void flow.apply()}>
          {t.apply}
        </button>
      </div>
    </>
  );
}

function Done({ step, flow, pick }: StepProps & { step: Extract<Step, { name: 'done' }> }) {
  const t = uiCopy(flow.shopId);
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      <div class="body">
        <p class="success">
          <IconCheck color="#2f7d5b" />
          {t.placed}
        </p>
        <div class="card-shot">
          <img
            class={loaded ? '' : 'loading'}
            src={step.cardPreviewUrl}
            alt={t.cardAlt}
            onLoad={() => setLoaded(true)}
          />
        </div>
        <p class="hint">{t.editHint}</p>
      </div>
      <div class="foot">
        <button
          type="button"
          class="btn btn--primary"
          onClick={() => {
            flow.close();
            // Na history.back() naar de gewone editor van deze order.
            window.setTimeout(() => openFullEditor(), 350);
          }}
        >
          <IconPencil />
          {t.edit}
        </button>
        <button
          type="button"
          class="link"
          onClick={() => {
            flow.chooseOther();
            pick();
          }}
        >
          {t.tryOther}
        </button>
      </div>
    </>
  );
}

function ErrorView({ step, flow, pick }: StepProps & { step: Extract<Step, { name: 'error' }> }) {
  const t = uiCopy(flow.shopId);
  const copy = errorCopy(step.code, step.retryAfterS, flow.shopId);
  const pickOther = () => {
    flow.chooseOther();
    pick();
  };
  let primary: { label: string; onClick: () => void };
  if (step.code === 'RATE_LIMITED' || step.code.startsWith('CATALOG_') || step.code === 'CONFIG_NOT_FOUND' || step.code === 'UNSUPPORTED_STYLE') primary = { label: t.ok, onClick: () => flow.close() };
  else if (step.retry === 'pick' || !copy.retrySame) primary = { label: t.chooseOtherPhoto, onClick: pickOther };
  else primary = { label: t.retry, onClick: () => void flow.retrySame() };
  const offerOther = primary.label === t.retry;
  return (
    <>
      <div class="body">
        <div class="err" role="alert">
          <div class="err__icon">
            <IconAlert />
          </div>
          <h3>{copy.title}</h3>
          <p>{copy.message}</p>
        </div>
      </div>
      <div class="foot">
        <button type="button" class="btn btn--primary" onClick={primary.onClick}>
          {primary.label}
        </button>
        {offerOther && (
          <button type="button" class="link" onClick={pickOther}>
            {t.pickOther}
          </button>
        )}
      </div>
    </>
  );
}
